<template>
    <vx-expandable-card>
        <template v-slot:header>Provisioning (SCIM)</template>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div class="text-sm text-slate-600">
                    Let your identity provider add members and revoke their single sign-on automatically. Unassigning or
                    deactivating someone at the identity provider signs them out of SSO and refuses their SSO logins as
                    soon as it sends the change; their role, OUs and Groups stay for when they are assigned again.
                    Assigning someone adds them without a role until an admin gives them one. Works with Microsoft Entra
                    ID and Okta. Google Workspace does not offer provisioning for custom apps.
                </div>

                <page-loading v-if="loading" />

                <vx-alert v-else-if="loadFailed" color="danger" :closeable="false" data-test="load-failed">
                    <div class="flex items-center justify-between">
                        The provisioning status could not be loaded.
                        <vx-button size="sm" color="danger" @click="load" data-test="retry-load">
                            Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                        </vx-button>
                    </div>
                </vx-alert>

                <vx-alert v-else-if="blocker" color="slate" :closeable="false" data-test="scim-blocker">
                    {{ blocker }}
                </vx-alert>

                <template v-else-if="scim">
                    <div class="grid gap-2 text-sm sm:grid-cols-[auto_1fr_auto] sm:items-center">
                        <span class="text-slate-500">Base URL</span>
                        <code class="break-all" data-test="base-url">{{ scim.base_url }}</code>
                        <vx-button size="xs" color="muted" @click="copyText(scim.base_url)" data-test="copy-base-url">
                            Copy <font-awesome-icon widthAuto :icon="faCopy" />
                        </vx-button>
                    </div>
                    <div class="text-xs text-slate-500">
                        Entra ID calls this the tenant URL, Okta the SCIM connector base URL. Users are matched on
                        <code>userName</code>, which must be the email address your identity provider sends for SSO.
                    </div>

                    <vx-alert v-if="generated" color="success" :closeable="false" data-test="generated-token">
                        <div class="font-bold">Copy the token now; it is shown only once.</div>
                        <div class="mt-2 flex flex-wrap items-center gap-2">
                            <code class="break-all" data-test="generated-token-value">{{ generated }}</code>
                            <vx-button size="xs" color="muted" @click="copyText(generated)" data-test="copy-token">
                                Copy <font-awesome-icon widthAuto :icon="faCopy" />
                            </vx-button>
                        </div>
                    </vx-alert>

                    <div
                        v-if="scim.token === null"
                        class="flex items-center justify-between gap-4"
                        data-test="no-token"
                    >
                        <span class="text-sm text-slate-500">No token yet.</span>
                        <vx-button size="sm" color="primary" @click="openModal('generate')" data-test="generate-token">
                            Generate token <font-awesome-icon widthAuto :icon="faKey" />
                        </vx-button>
                    </div>

                    <div v-else class="rounded-lg border border-slate-200 p-4 flex flex-col gap-3" data-test="token">
                        <div class="flex flex-wrap items-center gap-2">
                            <span class="font-bold">Bearer token</span>
                            <vx-badge
                                size="xs"
                                :color="scimTokenStateColor[scimTokenState(scim.token)]"
                                data-test="token-state"
                            >
                                {{ scimTokenStateLabel[scimTokenState(scim.token)] }}
                            </vx-badge>
                        </div>
                        <dl class="grid gap-1 text-sm sm:grid-cols-[140px_1fr]">
                            <dt class="text-slate-500">Created</dt>
                            <dd>{{ shortDateTime(scim.token.created_at) }}</dd>
                            <dt class="text-slate-500">Expires</dt>
                            <dd data-test="token-expires">{{ shortDateTime(scim.token.expires_at) }}</dd>
                            <dt class="text-slate-500">Last request</dt>
                            <dd data-test="token-last-request">
                                {{ scim.token.last_request_at ? fromNow(scim.token.last_request_at) : 'Never' }}
                            </dd>
                            <dt class="text-slate-500">Provisioned users</dt>
                            <dd>{{ scim.users_count }}</dd>
                        </dl>
                        <vx-alert
                            v-if="scimTokenState(scim.token) === 'expired'"
                            color="danger"
                            :closeable="false"
                            data-test="token-expired"
                        >
                            The token has expired. Your identity provider's requests are refused until you extend it or
                            generate a new one.
                        </vx-alert>
                        <div class="flex flex-wrap gap-2">
                            <vx-button
                                size="sm"
                                color="muted-dark"
                                @click="openModal('extend')"
                                data-test="extend-token"
                            >
                                Extend <font-awesome-icon widthAuto :icon="faClock" />
                            </vx-button>
                            <vx-button
                                size="sm"
                                color="muted-dark"
                                @click="openModal('regenerate')"
                                data-test="regenerate-token"
                            >
                                Regenerate <font-awesome-icon widthAuto :icon="faArrowsRotate" />
                            </vx-button>
                            <vx-button size="sm" color="muted" outline @click="revoke" data-test="revoke-token">
                                Revoke <font-awesome-icon widthAuto :icon="faTrash" />
                            </vx-button>
                        </div>
                    </div>

                    <div v-if="scim.events.length" class="flex flex-col" data-test="events">
                        <div class="font-bold text-sm mb-1">Recent activity</div>
                        <div
                            v-for="event in scim.events"
                            :key="event.id"
                            class="flex flex-wrap gap-x-1 text-sm py-2 border-b border-slate-100 last:border-0"
                            data-test="event"
                        >
                            <span class="font-bold">{{ scimEventLabel(event.action) }}</span>
                            <span v-if="event.user_name">{{ event.user_name }}</span>
                            <span v-if="event.reason" class="text-rose-700">
                                ({{ scimRefusalLabel(event.reason) }})
                            </span>
                            <span class="text-slate-500">{{ shortDateTime(event.created_at) }}</span>
                        </div>
                    </div>
                </template>
            </div>
        </template>
    </vx-expandable-card>

    <sso-scim-token-modal
        v-model:visible="modal"
        :teamId="props.teamId"
        :mode="modalMode"
        @generated="onGenerated"
        @extended="onExtended"
    />
</template>

<script lang="ts" setup>
import { faArrowsRotate, faClock, faCopy, faKey, faRefresh, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBadge, VxButton, VxExpandableCard, VxSwal } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { onMounted, ref } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useCopyText } from '~/composables/useCopyText';
import { ssoClient } from '~/services';
import type { SsoScim } from '~/services/ssoClient.types';
import { fromNow, shortDateTime } from '~/utils/date';
import { throwSwalRequestError } from '~/utils/request';
import SsoScimTokenModal, { type ScimTokenModalMode } from './SsoScimTokenModal.vue';
import {
    conflictText,
    scimEventLabel,
    scimRefusalLabel,
    scimTokenState,
    scimTokenStateColor,
    scimTokenStateLabel,
} from './ssoSettingsHelpers';

const props = defineProps<{
    teamId: number;
}>();

const { copyText } = useCopyText();

const scim = ref<SsoScim | null>(null);
const loading = ref(true);
const loadFailed = ref(false);
const blocker = ref<string | null>(null);
const generated = ref<string | null>(null);
const modal = ref(false);
const modalMode = ref<ScimTokenModalMode>('generate');
// A status read still in flight when a mutation answers would overwrite the newer state.
const statusCancelKey = Symbol('sso-scim-status');

const load = async () => {
    try {
        const { data } = await ssoClient.getScim(props.teamId, { cancelKey: statusCancelKey });
        scim.value = data;
        blocker.value = null;
        loadFailed.value = false;

        if (data.token === null) {
            generated.value = null;
        }
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        blocker.value = conflictText(error, {
            connection_missing: 'Connect an identity provider before setting up provisioning.',
        });
        loadFailed.value = blocker.value === null;
    } finally {
        loading.value = false;
    }
};

const openModal = (mode: ScimTokenModalMode) => {
    modalMode.value = mode;
    modal.value = true;
};

const onGenerated = (token: string, updated: SsoScim) => {
    ssoClient.cancelRequest(statusCancelKey);
    generated.value = token;
    scim.value = updated;
    loadFailed.value = false;
};

const onExtended = (updated: SsoScim) => {
    ssoClient.cancelRequest(statusCancelKey);
    scim.value = updated;
    loadFailed.value = false;
};

const revoke = () => {
    VxSwal.confirmation(
        'Revoke token',
        'Your identity provider will no longer be able to add members or revoke their single sign-on until you generate a new token.',
        'danger',
        () => ssoClient.revokeScimToken(props.teamId).then(load).catch(throwSwalRequestError)
    );
};

defineExpose({ load });

onMounted(load);
</script>
